'use client';

import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import ZeeloolPay from '@zeelool/pay';
import { useTranslations } from 'next-intl';
import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Dialog from '../Dialog';
import Popup from '../Popup';
import WapOnly from '../Responsive/WapOnly';
import WebOnly from '../Responsive/WebOnly';
import { usePayCenterTrack, type FormValidStatus } from './hook/usePayCenterTrack';
interface PayCenterProps {
  totalPrice: string;
  from?: 'payment' | 'diff' | 'vip';
  symbol_prefix?: string;
  symbol_suffix?: string;
  onSuccess: (order: Record<string, any>) => void;
  onError: (errorMsg?: string) => void;
  children?: React.ReactNode;
  currency?: string;
}

const PayCenter = forwardRef(
  (
    {
      totalPrice,
      from = 'payment',
      onSuccess,
      onError,
      symbol_prefix,
      symbol_suffix,
      children,
      currency,
    }: PayCenterProps,
    ref,
  ) => {
    const t = useTranslations('');
    const [dialog, setDialog] = useState(false);
    const [checkoutInstance, setCheckoutInstance] = useState<any>(null);
    const [formValidStatus, setFormValidStatus] = useState<FormValidStatus>({
      card_number: '',
      expiry_date: '',
      cvv: '',
    });
    // const currency = useStore((state) => state.currency);
    const payRef = useRef<HTMLDivElement>(null);
    const { deviceType } = useContext(ResponsiveContext);
    const isWeb = [EPlatform.WEB].includes(deviceType);

    // 获取store中的数据
    const token = useStore((state) => state.token);

    // 埋点追踪 hooks
    const {
      trackCardPopUpExp,
      trackCardPopUpCloseBtnClick,
      trackCardPopUpPayBtnClick,
      trackCardPopUpPayResult,
    } = usePayCenterTrack();

    // 清理资源
    useEffect(() => {
      return () => {
        if (checkoutInstance) {
          // 在组件卸载时做清理，如果需要的话
        }
      };
    }, [checkoutInstance]);

    const close = () => {
      // 埋点：弹窗关闭点击
      trackCardPopUpCloseBtnClick(formValidStatus);
      onError();
      setDialog(false);
    };

    // 根据from返回对应的订单分类
    const getOrderCategory = () => {
      if (from === 'diff') return 2;
      if (from === 'vip') return 1;
      return 0;
    };

    const initConfig = (order: Record<string, any>) => {
      const config = {
        env: env.NEXT_PUBLIC_PAY_CENTER_ENV,
        locale: env.NEXT_PUBLIC_SITE_LANG,
        order_id: order.order_id,
        order_category: getOrderCategory(),
        order_amount: formatPriceNumberWithSymbol(totalPrice, currency, true),
        full_currency: currency || '',
        symbol_prefix: encodeURIComponent(symbol_prefix || ''),
        symbol_suffix: encodeURIComponent(symbol_suffix || ''),
        token: token || '',
        site: env.NEXT_PUBLIC_PAY_CENTER_SITE_NAME,
        domain: typeof window !== 'undefined' ? window.location.origin : '',
        store_id: isWeb ? '1' : '4',
        onInit: () => logger.log('[PayCenter] onInit'),
        onChange: (data: any) => handleOnChange(data),
        onClick: (data) => handleOnClick(data),
        onSubmit: (data: any) => onSubmit(data),
        onError: () => onError(),
      };
      return config;
    };

    const postFormData = (url: string, data: Record<string, any>) => {
      if (typeof document === 'undefined') return;

      const form = document.createElement('form');
      form.action = url;
      form.method = 'POST';
      form.style.display = 'none';
      form.encoding = 'x-www-form-urlencoded';

      Object.keys(data).forEach((key) => {
        const opt = document.createElement('input');
        opt.name = key;
        opt.value = data[key];
        form.appendChild(opt);
      });

      const submit = document.createElement('input');
      submit.type = 'submit';
      submit.value = 'submit';
      form.appendChild(submit);

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    };

    interface SubmitResponse {
      code: number;
      data: {
        status: string;
        payment_no?: string;
        error_message?: string;
        three_ds?: {
          auth: string;
          redirect_method: string;
          redirect_url: string;
          redirect_data: Record<string, any>;
        };
      };
      msg: string;
    }

    const onSubmit = ({ code, data, msg }: SubmitResponse) => {
      if (code === 200) {
        try {
          // 支付成功
          if (data.status === 'processing') {
            // 埋点：支付成功
            trackCardPopUpPayResult(true);
            onSuccess(data);
          } else if (data.status === 'three_ds' && data.three_ds?.auth === 'Redirect') {
            // 出发 3ds 校验
            if (typeof localStorage !== 'undefined' && data.payment_no) {
              localStorage.setItem('zl-pay-center-no', data.payment_no);
            }
            if (data.three_ds?.redirect_method === 'GET') {
              window.location.replace(data.three_ds.redirect_url);
            } else if (data.three_ds?.redirect_method === 'POST') {
              // 创建 form 表单
              postFormData(data.three_ds.redirect_url, data.three_ds.redirect_data);
            }
          } else {
            // 其他均为支付失败
            // 埋点：支付失败
            trackCardPopUpPayResult(false, data.error_message);
            onError(data.error_message);
          }
        } catch (error) {
          logger.error(error);
          // 埋点：支付异常失败
          trackCardPopUpPayResult(false, data.error_message);
          onError(data.error_message);
        }
      } else {
        // 埋点：请求失败
        trackCardPopUpPayResult(false, msg);
        onError(msg);
      }
    };

    interface ValidationData {
      card_number: string;
      expiry_date: string;
      cvv: string;
    }

    const handleOnChange = (data: ValidationData) => {
      logger.log('@handleOnChange1', data);
      // 更新表单验证状态用于埋点
      setFormValidStatus({
        card_number: data.card_number || '',
        expiry_date: data.expiry_date || '',
        cvv: data.cvv || '',
      });
    };

    const handleOnClick = (data: ValidationData) => {
      setFormValidStatus({
        card_number: data.card_number || '',
        expiry_date: data.expiry_date || '',
        cvv: data.cvv || '',
      });
      // 埋点：支付按钮点击
      trackCardPopUpPayBtnClick({
        card_number: data.card_number || '',
        expiry_date: data.expiry_date || '',
        cvv: data.cvv || '',
      });
    };

    // const payAction = () => {
    //   setDialog(true);

    //   // 在下一个渲染周期挂载支付组件
    //   setTimeout(() => {
    //     if (payRef.current) {
    //       const config = initConfig();
    //       const instance = new ZeeloolPay(config);
    //       instance.mount('pay-center');
    //       setCheckoutInstance(instance);
    //     }
    //   }, 0);
    // };

    useImperativeHandle(ref, () => ({
      payAction: (order: Record<string, any>) => {
        setDialog(true);
        // 埋点：弹窗曝光
        trackCardPopUpExp();
        // 在下一个渲染周期挂载支付组件
        setTimeout(() => {
          // if (payRef.current) {
          //   const config = initConfig();
          //   const instance = new ZeeloolPay(config);
          //   instance.mount('pay-center');
          //   setCheckoutInstance(instance);
          // }
          const config = initConfig(order);
          const instance = new ZeeloolPay(config);
          instance.mount('pay-center');
          setCheckoutInstance(instance);
        }, 220);
      },
    }));

    return (
      <>
        <WebOnly>
          <div>
            <Dialog.Info
              open={dialog}
              onClose={close}
              maxWidth="md"
              showTitleDivider={false}
              closeOnClickOverlay={false}
              title={t('payment.title')}
              sx={{
                '& .MuiPaper-root': {
                  width: '650px',
                  maxWidth: '650px',
                  margin: 'auto',
                },
                '& .MuiDialogTitle-root': {
                  cursor: 'default',
                  fontWeight: 'bold',
                  textAlign: 'center',
                },
              }}
            >
              <div
                id="pay-center"
                ref={payRef}
                style={{ width: '100%', minHeight: '300px', paddingTop: '10px' }}
              />
            </Dialog.Info>
          </div>
        </WebOnly>
        <WapOnly>
          <Popup
            open={dialog}
            onClose={close}
            title={t('payment.title')}
            bodyClassName="min-h-[300%] !px-[10px]"
            showDivider={false}
            closeOnClickOverlay={false}
            sx={{
              '& .MuiDialog-paper': {
                width: '650px',
                margin: 'auto',
              },
              '& .MuiDialogTitle-root': {
                cursor: 'default',
                fontWeight: 'bold',
                textAlign: 'center',
              },
            }}
          >
            <div id="pay-center" ref={payRef} style={{ width: '100%', minHeight: '300px' }} />
          </Popup>
        </WapOnly>
        {children && children}
      </>
    );
  },
);

PayCenter.displayName = 'PayCenter';

export default PayCenter;
